import React, { useEffect, useMemo, useState } from 'react';
import { Switch, Route } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ApplicationState } from '../../../store/rootReducer';
import {
  fetchBlueprintsThunk,
  createBlueprintThunk,
  applyBlueprintThunk,
  deleteBlueprintThunk,
} from '../../../store/blueprints/blueprintsActions';
import {
  setSearchTerm,
  toggleTagFilter,
  setViewMode,
  openEditor,
  closeEditor,
  openApply,
  closeApply,
  openDetailDrawer,
  closeDetailDrawer,
} from '../../../store/blueprints/blueprintsReducer';
import { emptyDraft } from '../../../store/blueprints/blueprintsTypes';
import { filterBlueprints, getTagsByFrequency } from '../../../store/blueprints/blueprintsUtil';
import { hasUserAdminRoles } from '../../../utils/bzmUserConfigUtil';
import { loadDevOptions } from '../../../utils/localStorageUtil';
import BlueprintsHero from './BlueprintsHero';
import BlueprintsToolbar from './BlueprintsToolbar';
import BlueprintTagFilter from './BlueprintTagFilter';
import BlueprintDetailContent from './BlueprintDetailContent';
import SideDrawer from '../../../components/overlays/SideDrawer';
import BlueprintGrid from './BlueprintGrid';
import BlueprintsListTable from './BlueprintsListTable';
import BlueprintFormModal from './BlueprintFormModal';
import BlueprintApplyModal from './BlueprintApplyModal';
import BlueprintDetailView from './BlueprintDetailView';
import BlueprintEditView from './BlueprintEditView';
import BlazePagination from '../../../components/ui/pagination/BlazePagination';
import SimpleLoader from '../../../components/loaders/SimpleLoader';
import { BLUEPRINTS_ROOT } from './blueprintRoutes';
import MixpanelService from '../../../utils/mixpanelService';
import { BLUEPRINTS_VIEWED, BLUEPRINT_DETAIL_OPENED, BLUEPRINT_VIEW_MODE_CHANGED } from '../../../utils/mixpanelEvents';

const GRID_PAGE_SIZE = 24;

const Blueprints = () => {
  const { t } = useTranslation('blueprints');
  const dispatch = useDispatch();
  const history = useHistory();
  const state = useSelector((s: ApplicationState) => s.blueprints);
  const blazeUserConfig = useSelector((s: ApplicationState) => s.blazeUserConfig.blazeUserConfig);

  // Grid local page state — resets to 1 when filters or view mode change.
  const [gridPage, setGridPage] = useState(1);

  useEffect(() => {
    MixpanelService.track(BLUEPRINTS_VIEWED());
    dispatch(fetchBlueprintsThunk() as never);
  }, [dispatch]);

  // Reset grid to page 1 whenever filters or view mode change.
  useEffect(() => {
    setGridPage(1);
  }, [state.filter.source, state.filter.searchTerm, state.filter.tags, state.viewMode]);

  const visible = useMemo(() => filterBlueprints(state.blueprints, state.filter), [state.blueprints, state.filter]);
  const tagsByFreq = useMemo(() => getTagsByFrequency(state.blueprints), [state.blueprints]);

  // Client-side page slices.
  const gridPageItems = useMemo(() => {
    const start = (gridPage - 1) * GRID_PAGE_SIZE;
    return visible.slice(start, start + GRID_PAGE_SIZE);
  }, [visible, gridPage]);

  const canPublishAccount = hasUserAdminRoles(blazeUserConfig);
  // Mirror the Edit-blueprint guard: admin role + the blueprint dev-options feature flag.
  const canCreateBlueprint = canPublishAccount && loadDevOptions()?.enableBlueprintEdit === true;

  const openDetail = (id: string) => {
    MixpanelService.track(BLUEPRINT_DETAIL_OPENED(state.viewMode));
    dispatch(openDetailDrawer(id));
  };
  const changeViewMode = (mode: typeof state.viewMode) => {
    MixpanelService.track(BLUEPRINT_VIEW_MODE_CHANGED(mode));
    dispatch(setViewMode(mode));
  };
  const detailBlueprint = state.blueprints.find(b => b.id === state.detailBlueprintId) || null;
  const startCreate = () => dispatch(openEditor({ ...emptyDraft }));

  const renderList = () => {
    if (state.isLoading) {
      return (
        <div className="card">
          <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
            <SimpleLoader />
          </div>
        </div>
      );
    }
    return (
      <div className="card">
        <div className="card-body">
          <BlueprintsHero searchTerm={state.filter.searchTerm} onSearchChange={term => dispatch(setSearchTerm(term))} />
          <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
            <div className="flex-grow-1">
              <BlueprintTagFilter
                tags={tagsByFreq}
                activeTags={state.filter.tags}
                onToggleTag={tag => dispatch(toggleTagFilter(tag))}
              />
            </div>
            <BlueprintsToolbar
              viewMode={state.viewMode}
              onViewModeChange={changeViewMode}
              onCreate={startCreate}
              canCreate={canCreateBlueprint}
            />
          </div>
          <h2 className="mt-4 mb-4 text-uppercase">
            {t('title')} ({visible.length})
          </h2>
          {state.viewMode === 'GRID' ? (
            <>
              <BlueprintGrid
                blueprints={gridPageItems}
                onOpen={openDetail}
                onAddToCatalog={id => dispatch(openApply(id))}
              />
              {visible.length > GRID_PAGE_SIZE && (
                <BlazePagination
                  pageSize={GRID_PAGE_SIZE}
                  total={visible.length}
                  activePage={gridPage}
                  pageSelectedHandler={setGridPage}
                />
              )}
            </>
          ) : (
            <BlueprintsListTable
              blueprints={visible}
              onOpen={openDetail}
              onAddToCatalog={id => dispatch(openApply(id))}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Switch>
        <Route exact path={BLUEPRINTS_ROOT} render={renderList} />
        <Route exact path={`${BLUEPRINTS_ROOT}/:id`} component={BlueprintDetailView} />
        <Route path={`${BLUEPRINTS_ROOT}/:id/edit`} component={BlueprintEditView} />
      </Switch>

      {state.editor.open && state.editor.draft && (
        <BlueprintFormModal
          initialDraft={state.editor.draft}
          canPublishAccount={canPublishAccount}
          onSave={(draft, serviceId) => dispatch(createBlueprintThunk(draft, serviceId) as never)}
          onCancel={() => dispatch(closeEditor())}
        />
      )}

      {state.applyBlueprintId &&
        (() => {
          const applyBp = state.blueprints.find(b => b.id === state.applyBlueprintId);
          return applyBp ? (
            <BlueprintApplyModal
              blueprintName={applyBp.name}
              onApply={(target, skip) => dispatch(applyBlueprintThunk(applyBp.id, target, skip) as never)}
              onCancel={() => dispatch(closeApply())}
            />
          ) : null;
        })()}

      <SideDrawer
        open={Boolean(state.detailBlueprintId)}
        onClose={() => dispatch(closeDetailDrawer())}
        width={860}
        footer={
          detailBlueprint ? (
            <div className="d-flex align-items-center justify-content-between" style={{ gap: 12 }}>
              <span className="text-truncate font-weight-bold">{detailBlueprint.name}</span>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  dispatch(closeDetailDrawer());
                  dispatch(openApply(detailBlueprint.id));
                }}
              >
                {t('detail.add')}
              </button>
            </div>
          ) : (
            undefined
          )
        }
      >
        {state.detailBlueprintId && (
          <BlueprintDetailContent
            id={state.detailBlueprintId}
            closeLabel={t('detail.close')}
            closeAsIcon
            showAddButton={false}
            onClose={() => dispatch(closeDetailDrawer())}
            onEdit={id => {
              dispatch(closeDetailDrawer());
              history.push(`${BLUEPRINTS_ROOT}/${id}/edit`);
            }}
            onDelete={id => {
              dispatch(deleteBlueprintThunk(id) as never);
              dispatch(closeDetailDrawer());
            }}
            onAddToCatalog={id => {
              dispatch(closeDetailDrawer());
              dispatch(openApply(id));
            }}
          />
        )}
      </SideDrawer>
    </>
  );
};

export default Blueprints;
